import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { DOBInputProps } from "./DOBInput.types";
import Select from "../../atoms/Select/Select";
import { SelectOption } from "../../atoms/Select/Select.types";

import "./DOBInput.scss";

const DOBInput: React.FC<DOBInputProps> = ({
  value,
  onChange,
  disabled,
  loading,
}) => {
  const yearOptions = getYears(1990, dayjs(new Date()).get("year"));

  const [valid, setValid] = useState(false);

  const [month, setMonth] = useState<number | undefined>();
  const [dayList, setDayList] = useState<Array<SelectOption>>([]);
  const [day, setDay] = useState<number | undefined>();
  const [year, setYear] = useState<number | undefined>();

  useEffect(() => {
    if (value === undefined) return;

    const djs = dayjs;
    djs.extend(utc);
    const date = djs.utc(value);

    const m = date.get("month");
    const d = date.get("date");

    const y = date.get("year");

    if (!isNaN(m)) setMonth(m);
    if (!isNaN(d)) setDay(d - 1);
    if (!isNaN(y))
      setYear(yearOptions.findIndex((value) => value.label === y.toString()));
  }, [value]);

  useEffect(() => {
    if (month !== undefined) {
      const days = new Array(months[month].days).fill("").map((_, index) => {
        return { label: (index + 1).toString() };
      });
      setDayList(days);
      setDay(day);
      updateDate();
    }
  }, [month, day, year]);

  const updateDate = () => {
    if (month !== undefined && day !== undefined && year !== undefined) {
      const y = yearOptions[year].label;

      const date = `${y}-${month + 1}-${day + 1}`;
      onChange(new Date(date));
      setValid(true);
    }
  };

  return (
    <div className="mp-molecules-dobinput--main">
      {loading ? (
        <div className="skeleton-container">
          <Skeleton className="skeleton" inline={true} />
        </div>
      ) : (
        <>
          <div className="month-field" data-testid="month-field">
            <Select
              id="month_select"
              label="Month"
              options={months}
              onChange={setMonth}
              selectedIndex={month === undefined ? -1 : month}
              prompt={"select month"}
              valid={valid}
              disabled={disabled}
            />
          </div>
          <div className="day-field" data-testid="day-field">
            <Select
              id="day_select"
              label="Day"
              options={dayList}
              onChange={setDay}
              selectedIndex={day === undefined ? -1 : day}
              prompt={
                month === undefined ? "select a month first" : "select day"
              }
              valid={valid}
              disabled={disabled}
            />
          </div>
          <div className="year-field" data-testid="year-field">
            <Select
              id="year_select"
              label="Year"
              options={yearOptions}
              onChange={setYear}
              selectedIndex={year === undefined ? -1 : year}
              prompt={"select year"}
              valid={valid}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DOBInput;

const months = [
  {
    label: "January",
    short: "Jan",
    number: 1,
    days: 31,
  },
  {
    label: "February",
    short: "Feb",
    number: 2,
    days: 29,
  },
  {
    label: "March",
    short: "Mar",
    number: 3,
    days: 31,
  },
  {
    label: "April",
    short: "Apr",
    number: 4,
    days: 30,
  },
  {
    label: "May",
    short: "May",
    number: 5,
    days: 31,
  },
  {
    label: "June",
    short: "Jun",
    number: 6,
    days: 30,
  },
  {
    label: "July",
    short: "Jul",
    number: 7,
    days: 31,
  },
  {
    label: "August",
    short: "Aug",
    number: 8,
    days: 31,
  },
  {
    label: "September",
    short: "Sep",
    number: 9,
    days: 30,
  },
  {
    label: "October",
    short: "Oct",
    number: 10,
    days: 31,
  },
  {
    label: "November",
    short: "Nov",
    number: 11,
    days: 30,
  },
  {
    label: "December",
    short: "Dec",
    number: 12,
    days: 31,
  },
];

const getYears = (start: number, end: number) => {
  let dates = [];
  while (start <= end) {
    dates.push({ label: end.toString() });
    end -= 1;
  }

  return dates;
};
